<template>
    <div :class="{'pointer-events-none': disabled}">
        <div class="flex items-center justify-between">
            <label v-show="hasLabel" :for="id" class="block text-sm font-medium text-gray-700">{{ label }}<span v-show="required" class="text-teal font-bold">*</span></label>
            <span class="text-sm font-medium">{{ modelValue }}%</span>
        </div>
        <div class="mt-6">
            <div ref="rangeSlider" class="relative w-full h-2 bg-gray-200">
                <div ref="progressBar" :style="valueStyles.fillPos" class="h-2 bg-teal"></div>
                <div ref="handleBar" :style="valueStyles.handlePos" class="range-slider_handle-bar w-4 h-6 -ml-2 bg-white shadow rounded absolute top-1/2 transform -translate-y-1/2"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';

export default {
    props: {
        label: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default(){
                return 'range-field-' + Math.random().toString(36).substr(2, 9);
            }
        },
        name: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        classes: {
            type: String,
            default: ''
        },
        min: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: 100
        },
        step: {
            type: Number,
            default: 1
        },
        modelValue: {
            type: Number,
            default: 0
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }){
        const rangeSlider = ref(null);
        const progressBar = ref(null);
        const handleBar   = ref(null);
        const state       = reactive({
            active: false,
            limit: 0,
            grab: 0
        });

        function clamp(value, min, max){
            return Math.min(Math.max(value, min), max);
        }

        function getPositionFromValue(value){
            const diffMaxMin = props.max - props.min;
            const diffValMin = value - props.min;
            const percentage = diffValMin / diffMaxMin;
            const pos = Math.round(percentage * state.limit);

            return pos;
        }

        function getValueFromPosition(pos){
            const percentage = clamp(pos, 0, state.limit) / (state.limit || 1);
            const baseVal = props.step * Math.round(percentage * (props.max - props.min) / props.step);
            const value = baseVal + props.min;

            return clamp(value, props.min, props.max);
        }

        function position(e){
            const coordinate = !e.touches ? e.clientX : e.touches[0].clientX;
            const direction = rangeSlider.value.getBoundingClientRect()['left'];
            const pos = coordinate - direction - state.grab;
            const value = getValueFromPosition(pos);

            return value;
        }

        function coordinates(pos){
            const value     = getValueFromPosition(pos);
            const position  = getPositionFromValue(value);
            const handlePos = position + state.grab;
            const fillPos   = handlePos;

            return {
                fillPos: fillPos,
                handlePos: handlePos
            }
        }

        const handleUpdate = () => {
            if(!rangeSlider.value){
                return;
            }

            const sliderPos = rangeSlider.value.offsetWidth;
            const handlePos = handleBar.value.offsetWidth;

            state.limit = sliderPos - handlePos;
            state.grab = handlePos / 2;
        }

        const onStart = (e) => {
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onStop);
        }

        const onMove = (e) => {
            e.stopPropagation();
            e.preventDefault();

            state.active = true;

            let value = position(e);

            emit('update:modelValue', value);
        }

        const onStop = (e) => {
            state.active = false;

            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onStop);
        }

        const valueStyles = computed(() => {
            const position = getPositionFromValue(props.modelValue);
            const coords = coordinates(position);

            return {
                fillPos: { width: `${coords.fillPos}px` },
                handlePos: { left: `${coords.handlePos}px` }
            }
        });

        onMounted(() => {
            handleUpdate();

            const resizeObserver = new ResizeObserver(handleUpdate);
            resizeObserver.observe(rangeSlider.value);

            rangeSlider.value.addEventListener('mousedown', onMove);
            rangeSlider.value.addEventListener('mouseup', onStop);
            rangeSlider.value.addEventListener('touchmove', onMove);
            rangeSlider.value.addEventListener('touchend', onStop);

            handleBar.value.addEventListener('mousedown', onStart);
            handleBar.value.addEventListener('touchmove', onMove);
            handleBar.value.addEventListener('touchend', onStop);
        });

        return {
            rangeSlider,
            progressBar,
            handleBar,
            valueStyles
        }
    },
    computed: {
        hasLabel(){
            return this.label != '';
        },
    }
}
</script>

<style>
</style>
